/**
 * <ma-lazy-video> - defers a video's download until it approaches the viewport.
 *
 * There is no native lazy-loading for video: <video> has no `loading`
 * attribute, and `autoplay` makes the browser start fetching during page load
 * whatever `preload` says. So a rail of autoplaying clips downloads all of them
 * up front, including the ones scrolled off the end of a carousel.
 *
 * The markup therefore ships the sources in `data-src` and only a poster is
 * visible. This attaches the real sources when the tile gets close, then plays.
 * Leaving the viewport pauses playback - decoding video nobody is looking at
 * costs battery on mobile.
 *
 * Deliberately a plain HTMLElement rather than Horizon's Component base: this
 * needs none of the refs machinery, and not importing @theme/* keeps it working
 * across Horizon updates.
 */
class MaLazyVideo extends HTMLElement {
  #observer = null;
  #loaded = false;

  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;

    // Honour reduced motion: leave the poster in place and never autoplay.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    if (!('IntersectionObserver' in window)) {
      this.#load(video);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#load(video);
            video.play?.().catch(() => {
              /* autoplay can be refused (low power mode); poster remains */
            });
          } else if (this.#loaded) {
            video.pause?.();
          }
        }
      },
      // Start fetching a little before the tile is actually on screen so it is
      // playing by the time it arrives, without reaching the whole carousel.
      { rootMargin: '200px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {HTMLVideoElement} video */
  #load(video) {
    if (this.#loaded) return;
    this.#loaded = true;

    for (const source of this.querySelectorAll('source[data-src]')) {
      source.setAttribute('src', source.dataset.src ?? '');
      source.removeAttribute('data-src');
    }

    video.load();
  }
}

if (!customElements.get('ma-lazy-video')) {
  customElements.define('ma-lazy-video', MaLazyVideo);
}
